import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  Card,
  CardContent,
  Stack,
  Divider,
  Avatar,
  Badge
} from '@mui/material';
import {
  FileDownload as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  DataObject as JsonIcon,
  Assessment as ReportIcon,
  CalendarToday as CalendarIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckIcon,
  HourglassEmpty as LoadingIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { VocuherCategory } from '../../utils/common-utils';

interface ReusableReportHeaderProps {
  reportType: string;
  title?: string;
  subtitle?: string;
  recordCount?: number;
  isLoading?: boolean;
  onDownloadPDF?: () => void;
  onDownloadExcel?: () => void;
  onDownloadJSON?: () => void;
  onPrint?: () => void;
  onShare?: () => void;
  shareTooltip?: string;
  showPrint?: boolean;
  showShare?: boolean;
  customActions?: React.ReactNode;
  backTo?: string;
}

const ReusableReportHeader: React.FC<ReusableReportHeaderProps> = ({
  reportType,
  title,
  subtitle,
  recordCount,
  isLoading = false,
  onDownloadPDF,
  onDownloadExcel,
  onDownloadJSON,
  onPrint,
  onShare,
  shareTooltip = 'Share Report',
  showPrint = true,
  showShare = true,
  customActions,
  backTo = '/reports'
}) => {
  const navigate = useNavigate();
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);

  const getReportTitle = () => {
    if (title) return title;
    
    switch (reportType) {
      case VocuherCategory.SALE:
        return 'GSTR-1 Sales Summary';
      case VocuherCategory.PURCHASE:
        return 'GSTR-2 Purchase Summary';
      default:
        return 'Report Summary';
    }
  };

  const getReportConfig = () => {
    switch (reportType) {
      // case VocuherCategory.SALE:
      //   return {
      //     color: '#2563eb',
      //     gradient: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
      //     textColor: '#1e40af',
      //     bgColor: '#eff6ff',
      //     darkColor: '#1e40af',
      //     label: 'Sales Report',
      //     icon: '📊'
      //   };
      // case VocuherCategory.PURCHASE:
      //   return {
      //     color: '#059669',
      //     gradient: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)',
      //     textColor: '#047857',
      //     bgColor: '#ecfdf5',
      //     darkColor: '#047857',
      //     label: 'Purchase Report',
      //     icon: '🛒'
      //   };
      default:
        return {
          color: '#4b5563',
          gradient: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
          textColor: '#374151',
          bgColor: '#f9fafb',
          darkColor: '#374151',
          label: 'General Report',
          icon: '📈'
        };
    }
  };

  const config = getReportConfig();

  const handleDownloadMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleDownloadAction = (action: () => void | undefined) => {
    if (action) {
      action();
    }
    handleDownloadMenuClose();
  };

  const formatDate = () => {
    return new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Box sx={{
      background: config.gradient,
      color: config.textColor,
      p: 2,
      borderRadius: 2,
      mb: 2,
      position: 'relative'
    }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            {/* Left Section - Back Button, Title and Info */}
            <Stack direction="row" alignItems="center" spacing={2} flex={1}>
              <Tooltip title="Back">
                <IconButton
                  onClick={() => navigate(backTo)}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.8)',
                    color: config.textColor,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)',
                      transform: 'translateX(-2px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                  size="small"
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              
              <Avatar
                sx={{
                  bgcolor: 'rgba(255,255,255,0.8)',
                  color: config.textColor,
                  width: 36,
                  height: 36,
                  fontSize: '1.1rem'
                }}
              >
                {config.icon}
              </Avatar>
              
              <Box flex={1}>
                <Typography 
                  variant="h6" 
                  fontWeight="700"
                  sx={{ 
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    lineHeight: 1.3,
                    color: config.textColor,
                    mb: 0.3
                  }}
                >
                  {getReportTitle()}
                </Typography>
                
                <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap">
                  {subtitle && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CalendarIcon sx={{ fontSize: 12, color: config.textColor, opacity: 0.7 }} />
                      <Typography variant="caption" sx={{ color: config.textColor, opacity: 0.8 }}>
                        {subtitle}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Stack>

            {/* Right Section - Actions */}
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Custom Actions */}
              {customActions}

              {/* Quick Action Buttons */}
              {showPrint && onPrint && (
                <Tooltip title="Print Report" arrow>
                  <IconButton
                    size="small"
                    onClick={onPrint}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.6)',
                      color: config.textColor,
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.8)',
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <PrintIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {showShare && onShare && (
                <Tooltip title={shareTooltip} arrow>
                  <IconButton
                    size="small"
                    onClick={onShare}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.6)',
                      color: config.textColor,
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.8)',
                        transform: 'translateY(-1px)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <ShareIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}

              {/* Main Download Button */}
              <Button
                variant="contained"
                startIcon={<DownloadIcon fontSize="small" />}
                onClick={handleDownloadMenuOpen}
                disabled={isLoading}
                size="small"
                sx={{
                  bgcolor: config.color,
                  color: 'white',
                  fontWeight: 600,
                  px: 2,
                  py: 0.5,
                  borderRadius: 1.5,
                  textTransform: 'none',
                  fontSize: '0.85rem',
                  minWidth: 80,
                  '&:hover': {
                    bgcolor: config.darkColor,
                    transform: 'translateY(-1px)'
                  },
                  '&:disabled': {
                    bgcolor: '#e5e7eb',
                    color: '#9ca3af'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Export
              </Button>
            </Stack>
          </Stack>

        {/* Enhanced Download Menu */}
        <Menu
          anchorEl={downloadMenuAnchor}
          open={Boolean(downloadMenuAnchor)}
          onClose={handleDownloadMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              borderRadius: 2,
              minWidth: 220,
              boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
              border: '1px solid #e2e8f0'
            }
          }}
        >
          {onDownloadPDF && (
            <MenuItem 
              onClick={() => handleDownloadAction(onDownloadPDF)}
              sx={{ 
                gap: 2, 
                py: 2,
                '&:hover': { 
                  bgcolor: '#fee2e2',
                  '& .MuiSvgIcon-root': { transform: 'scale(1.1)' }
                }
              }}
            >
              <Avatar sx={{ bgcolor: '#fef2f2', color: '#dc2626', width: 36, height: 36 }}>
                <PdfIcon />
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight="600">
                  PDF Document
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Print-ready format
                </Typography>
              </Box>
            </MenuItem>
          )}
          
          {onDownloadExcel && (
            <MenuItem 
              onClick={() => handleDownloadAction(onDownloadExcel)}
              sx={{ 
                gap: 2, 
                py: 2,
                '&:hover': { 
                  bgcolor: '#f0fdf4',
                  '& .MuiSvgIcon-root': { transform: 'scale(1.1)' }
                }
              }}
            >
              <Avatar sx={{ bgcolor: '#f0fdf4', color: '#16a34a', width: 36, height: 36 }}>
                <ExcelIcon />
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight="600">
                  Excel Spreadsheet
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Data analysis format
                </Typography>
              </Box>
            </MenuItem>
          )}
          
          {onDownloadJSON && (
            <MenuItem 
              onClick={() => handleDownloadAction(onDownloadJSON)}
              sx={{ 
                gap: 2, 
                py: 2,
                '&:hover': { 
                  bgcolor: '#fef3c7',
                  '& .MuiSvgIcon-root': { transform: 'scale(1.1)' }
                }
              }}
            >
              <Avatar sx={{ bgcolor: '#fefce8', color: '#ca8a04', width: 36, height: 36 }}>
                <JsonIcon />
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight="600">
                  JSON Data
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Raw data format
                </Typography>
              </Box>
            </MenuItem>
          )}
        </Menu>
    </Box>
  );
};

export default ReusableReportHeader;